import React from 'react';
import { ArrowProcessIcon } from '@dynamika-shared/ui/icons';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { ReadingTasks } from './ReadingTasks/ReadingTasks';
import { QCTasks } from './QCTasks/QCTasks';
import { SubjectUploads } from './Uploads/SubjectUploads';
import { BuildArgs, SectionWithContent } from './common/types';
import { VisitStatusEnum } from '@dynamika-shared/types/status';
import { filterByVisitId, formatVisitDateLabel } from './common/renderers';

export function buildSectionsFromVisits({
  clinicalTrialId,
  patientId,
  visits,
  uploads,
  uploadsLoading,
  uploadsError,
  qcTasks,
  qcTasksLoading,
  qcTasksError,
  readingTasks,
  readingTasksLoading,
  readingTasksError,
  readingTaskDefinitions,
  onReopenVisitClick,
}: BuildArgs): SectionWithContent[] {
  const sorted = [...(visits ?? [])].sort(
    (a, b) => (a.visitOrder ?? 0) - (b.visitOrder ?? 0)
  );

  return sorted.map((v): SectionWithContent => {
    const sectionKey = v.id || `visit-${v.visitOrder}`;
    const titleText = v.visitName || `Visit ${v.visitOrder}`;
    const taskId = v.taskId;
    const visitDateLabel = v.visitDate ? formatVisitDateLabel(v.visitDate) : '';
    const visitStatus = (v.visitStatus || undefined) as
      | VisitStatusEnum
      | undefined;

    const hasTaskId =
      typeof taskId === 'string' ? taskId.trim().length > 0 : Boolean(taskId);

    const uploadsForVisit = filterByVisitId(uploads, v.id);
    const qcTasksForVisit = filterByVisitId(qcTasks, v.id);
    const readingTasksForVisit = filterByVisitId(readingTasks, v.id);
    const readingDefsForVisit = filterByVisitId(readingTaskDefinitions, v.id);

    return {
      key: sectionKey,
      title: (
        <div className='flex items-center gap-2'>
          <span className='font-semibold text-[20px]'>{titleText}</span>

          {visitStatus && (
            <StatusPill
              className='font-semibold text-[13px]'
              status={visitStatus}
            />
          )}
        </div>
      ),
      visitDateLabel,
      visitStatus,
      actions:
        hasTaskId && onReopenVisitClick && visitStatus === 'CLOSED' ? (
          <button
            type='button'
            className='flex items-center gap-1 text-dodgerBlue text-[14px] font-semibold'
            onClick={e => {
              e.stopPropagation();
              onReopenVisitClick({ visitId: v.id, taskId });
            }}
          >
            <ArrowProcessIcon className='w-2.5 h-2.5' />
            Reopen Visit
          </button>
        ) : null,
      innerCollapsible: ['uploads', 'qc', 'readings'],
      content: [
        {
          key: 'uploads',
          label: 'Uploads',
          body: (
            <SubjectUploads
              clinicalTrialId={clinicalTrialId}
              patientId={patientId}
              visitId={v.id}
              uploads={uploadsForVisit}
              loading={uploadsLoading}
              error={uploadsError}
            />
          ),
        },
        {
          key: 'qc',
          label: 'QC',
          body: (
            <QCTasks
              clinicalTrialId={clinicalTrialId}
              patientId={patientId}
              visitId={v.id}
              tasks={qcTasksForVisit}
              loading={qcTasksLoading}
              error={qcTasksError}
            />
          ),
        },
        {
          key: 'readings',
          label: 'Readings',
          body: (
            <ReadingTasks
              clinicalTrialId={clinicalTrialId}
              patientId={patientId}
              visitId={v.id}
              tasks={readingTasksForVisit}
              taskDefinitions={readingDefsForVisit}
              loading={readingTasksLoading}
              error={readingTasksError}
            />
          ),
        },
      ],
    };
  });
}
